import React from 'react';
import {useTranslation} from 'react-i18next';
import {StatGrid} from '../primitives/StatGrid';
import {DetailFooter} from '../primitives/DetailFooter';
import {LevelBadge} from '../primitives/LevelBadge';
import {isPlainObject} from '../shapes';
import {humanizeFieldLabel, humanizeValue, isNumeric} from '../humanize';
import {
    getIndicatorConfig,
    indicatorLabelKey,
    alertTone,
    alertSeverityLevel,
    affectedItem,
    leftoverFields,
    isSafeHttpUrl,
    splitOnToken,
    URL_TOKEN,
} from '../alert_indicators';

// Detalle de las alertas (ALERT_TRIGGERED) — el ~65% de la bitácora. Patrón de 3 bloques:
//   ① frase-historia con el número clave + badge de nivel,
//   ② mini-stats jerarquizados (el DESVÍO primero) y, en cost_vs_time, dos barras apiladas,
//   ③ pie de contexto (ítem afectado · prioridad · ocurrencias).
// Qué muestra cada indicador lo declara `alert_indicators.js`; acá solo se pinta. Todo
// degrada: sin datos para la frase se muestran los stats, y los campos que ningún indicador
// declaró caen como filas label/valor al final (nunca desaparecen ni salen como JSON).

// Cada hook de la config se llama aislado: un payload raro apaga ESE bloque, no el panel.
function safe(build, fallback) {
    try {
        const result = build();
        return result === null || result === undefined ? fallback : result;
    } catch (e) {
        return fallback;
    }
}

// La frase lleva los números en <strong> DENTRO del value de i18n, así que se inyecta como
// HTML. Se deja el `escapeValue` por DEFECTO (true) a propósito: i18next escapa los valores
// interpolados (nombres de cuenta, métricas, URLs) pero NO el template, así que el <strong>
// funciona, un nombre con markup no puede inyectar nada, y al renderizarse como HTML las
// entidades (&#x2F;, &#39;) se ven como el carácter original.
function StoryHtml({html}) {
    return <span dangerouslySetInnerHTML={{__html: html}}/>;
}

// Link truncado. Solo se hace <a> si el esquema es http(s) — un `javascript:` guardado en el
// detail no se convierte en href.
function UrlLink({url}) {
    if (!isSafeHttpUrl(url)) return <span className="ald-url">{url}</span>;
    return <a className="ald-url" href={url} target="_blank" rel="noreferrer">{url}</a>;
}

function StoryLine({story, level, t}) {
    const html = t(story.key, story.values);
    const badge = <LevelBadge level={level}/>;
    if (!story.url) {
        return <div className="ald-story"><StoryHtml html={html}/> {badge}</div>;
    }
    const [before, after] = splitOnToken(html, URL_TOKEN);
    return (
        <div className="ald-story">
            {before ? <StoryHtml html={before}/> : null}
            <UrlLink url={story.url}/>
            {after ? <StoryHtml html={after}/> : null} {badge}
        </div>
    );
}

function clampWidth(value) {
    return Math.max(0, Math.min(100, Number(value)));
}

// Barras finas apiladas con el % SIEMPRE en texto al lado (nunca solo la barra).
function ProgressBars({bars}) {
    const visible = (bars || []).filter(bar => bar && isNumeric(bar.pct) && bar.text);
    if (!visible.length) return null;
    return (
        <div className="ald-progress">
            {visible.map(bar => (
                <div className="ald-progress-row" key={bar.key || bar.label}>
                    <span className="ald-progress-label">{bar.label}</span>
                    <span className="ald-progress-track">
                        <span
                            className={`ald-progress-fill -${bar.tone || 'normal'}`}
                            style={{width: `${clampWidth(bar.pct)}%`}}
                        />
                    </span>
                    <span className="ald-progress-value">{bar.text}</span>
                </div>
            ))}
        </div>
    );
}

// Filas genéricas: campos presentes que ningún indicador declaró (+ el nombre del indicador
// cuando no hay frase). Lo irreducible a texto se omite: acá no entra una llave.
function GenericRows({rows}) {
    if (!rows.length) return null;
    return (
        <table className="activity-log-detail-table">
            <tbody>
            {rows.map(row => (
                <tr key={row.key}>
                    <td className="field-label">{row.label}</td>
                    <td className="field-after">{row.value}</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

function formatDate(value, language) {
    if (!value) return null;
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return null;
    try {
        return date.toLocaleDateString(language || undefined);
    } catch (e) {
        return date.toLocaleDateString();
    }
}

// Ráfaga colapsada: el contador vive en el ENTRY, no en el detail.
function occurrencesText(entry, t, language) {
    const count = entry && entry.occurrences;
    if (!isNumeric(count) || Number(count) <= 1) return null;
    const first = formatDate(entry.first_seen, language);
    const last = formatDate(entry.last_seen, language);
    if (first && last) {
        return t('activity_log.detail.occurrences_range', {
            count: Number(count), first, last, interpolation: {escapeValue: false},
        });
    }
    return t('activity_log.detail.occurrences_simple', {count: Number(count)});
}

function buildGenericRows(detail, config, hasStory, t) {
    const rows = leftoverFields(detail, config)
        .map(([field, value]) => ({key: field, label: humanizeFieldLabel(field, t), value: humanizeValue(field, value, t)}))
        .filter(row => row.value !== null && row.value !== undefined && row.value !== '');
    // Sin frase, el panel tiene que decir de qué alerta se trata (nunca con el enum crudo).
    const labelKey = indicatorLabelKey(detail.indicator);
    if (!hasStory && labelKey) {
        rows.unshift({key: 'indicator', label: humanizeFieldLabel('indicator', t), value: t(labelKey)});
    }
    return rows;
}

export function AlertDetail({detail, entry}) {
    const {t, i18n} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const config = getIndicatorConfig(detail.indicator);
    const tone = alertTone(detail);
    // Con el nivel con que se emite cuando el `level` no es una gravedad: el ODS es siempre crítico.
    const level = alertSeverityLevel(detail);

    const story = config && config.story ? safe(() => config.story(detail, t), null) : null;
    const stats = config && config.stats ? safe(() => config.stats(detail, t, tone), []) : [];
    const bars = config && config.bars ? safe(() => config.bars(detail, t, tone), []) : [];
    const priority = config && config.priority ? safe(() => config.priority(detail), null) : null;
    const rows = safe(() => buildGenericRows(detail, config, !!story, t), []);
    const item = safe(() => affectedItem(detail, t), null);
    const occurrences = safe(() => occurrencesText(entry, t, i18n && i18n.language), null);

    // Sin frase, el badge de nivel se muestra suelto para no perder la severidad.
    const looseBadge = !story ? <LevelBadge level={level}/> : null;

    const footerParts = [];
    if (item) {
        footerParts.push(
            <span className="ald-footer-item" key="item">
                {item.type
                    ? t('activity_log.detail.affected_item', {
                        type: item.type, name: item.name, interpolation: {escapeValue: false},
                    })
                    : item.name}
            </span>,
        );
    }
    if (priority) {
        footerParts.push(
            <span className="ald-chip" key="priority">
                {t('activity_log.detail.goal_priority', {n: priority, interpolation: {escapeValue: false}})}
            </span>,
        );
    }
    if (occurrences) footerParts.push(<span className="ald-footer-occurrences" key="occurrences">{occurrences}</span>);

    return (
        <div className="ald-alert">
            {detail._spurious ? <div className="ald-spurious">{t('activity_log.detail.spurious')}</div> : null}
            {story ? <StoryLine story={story} level={level} t={t}/> : looseBadge}
            <ProgressBars bars={bars}/>
            <StatGrid stats={stats}/>
            <GenericRows rows={rows}/>
            {footerParts.length ? (
                <DetailFooter>
                    {footerParts.map((part, index) => (
                        <React.Fragment key={part.key}>
                            {index > 0 ? <span className="ald-footer-sep"> · </span> : null}
                            {part}
                        </React.Fragment>
                    ))}
                </DetailFooter>
            ) : null}
        </div>
    );
}
